import { useEffect } from "react";

/**
 * JsonLd injects a single JSON-LD <script> tag into <head> for the lifetime
 * of the component, then cleans up on unmount. Use one per page-specific schema.
 */
export default function JsonLd({ data, id }) {
  useEffect(() => {
    if (!data) return;
    const script = document.createElement("script");
    script.type = "application/ld+json";
    if (id) script.setAttribute("data-jsonld-id", id);
    script.textContent = JSON.stringify(data);
    document.head.appendChild(script);
    return () => {
      try { document.head.removeChild(script); } catch {}
    };
  }, [data, id]);
  return null;
}

const SITE = "https://www.coastalbendcontractor.com";

export function breadcrumb(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: it.url?.startsWith("http") ? it.url : `${SITE}${it.url || ""}`,
    })),
  };
}

export function serviceSchema(s) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: s.title,
    provider: { "@id": `${SITE}/#business` },
    areaServed: { "@type": "AdministrativeArea", name: "Nueces County, TX" },
    description: s.description,
    url: `${SITE}/services#${s.key}`,
    image: s.image,
  };
}

export function projectListSchema(projects) {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement: projects.slice(0, 20).map((p, i) => ({
      "@type": "ListItem",
      position: i + 1,
      url: `${SITE}/projects/${p.id}`,
      name: p.title,
      image: p.cover_image,
    })),
  };
}

export function floorPlanSchema(p) {
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    "@id": `${SITE}/floor-plans/${p.id}`,
    name: `${p.name} Floor Plan`,
    description: p.description,
    image: p.image_url,
    brand: { "@type": "Brand", name: "Coastal Bend Contractor Co." },
    category: "House Floor Plan",
    additionalProperty: [
      { "@type": "PropertyValue", name: "Square Feet", value: p.sqft },
      { "@type": "PropertyValue", name: "Bedrooms", value: p.bedrooms },
      { "@type": "PropertyValue", name: "Bathrooms", value: p.bathrooms },
      { "@type": "PropertyValue", name: "Garage", value: `${p.garage}-car` },
      { "@type": "PropertyValue", name: "Stories", value: p.stories },
    ],
    offers: {
      "@type": "Offer",
      priceCurrency: "USD",
      price: p.price_from,
      priceSpecification: {
        "@type": "PriceSpecification",
        priceCurrency: "USD",
        price: p.price_from,
        valueAddedTaxIncluded: false,
      },
      availability: "https://schema.org/InStock",
      url: `${SITE}/floor-plans/${p.id}`,
      seller: { "@id": `${SITE}/#business` },
    },
  };
}

export function reviewSchema(t) {
  return {
    "@context": "https://schema.org",
    "@type": "Review",
    itemReviewed: { "@id": `${SITE}/#business` },
    reviewBody: t.content,
    reviewRating: { "@type": "Rating", ratingValue: t.rating, bestRating: 5 },
    author: { "@type": "Person", name: t.client_name },
    datePublished: t.created_at?.slice(0, 10),
  };
}
